import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminNavigation from './AdminNavigation';
import './OrderManagement.css';
import { API_URL } from '../utils/config';


const OrderManagement = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/img/products/f1.jpg';
    if (imagePath.startsWith('/uploads/')) {
      return `${API_URL}${imagePath}`;
    }
    return imagePath;
  };
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;

  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    fetchOrders();
  }, [navigate]);

  const fetchOrders = async () => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      // Use /api/admin/orders
      const response = await axios.get(`${API_URL}/api/admin/orders`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      setOrders(response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setMessage({ text: 'Error loading orders', type: 'error' });
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      // Use /api/admin/orders
      await axios.put(
        `${API_URL}/api/admin/orders/${orderId}`,
        { status: newStatus },
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );
      
      setMessage({ text: 'Order status updated successfully!', type: 'success' });
      fetchOrders();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error updating order:', error);
      setMessage({ text: 'Error updating order status', type: 'error' });
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        const adminToken = localStorage.getItem('adminToken');
        // Use /api/admin/orders
        await axios.delete(`${API_URL}/api/admin/orders/${orderId}`, {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        });
        
        setMessage({ text: 'Order deleted successfully!', type: 'success' });
        fetchOrders();
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        console.error('Error deleting order:', error);
        setMessage({ text: 'Error deleting order', type: 'error' });
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#ffa500';
      case 'processing':
        return '#2196f3';
      case 'shipped':
        return '#9c27b0';
      case 'delivered':
        return '#4caf50';
      case 'cancelled':
        return '#f44336';
      default:
        return '#666';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'processing':
        return '⚙️';
      case 'shipped':
        return '🚚';
      case 'delivered':
        return '✅';
      case 'cancelled':
        return '❌';
      default:
        return '📦';
    }
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    const matchesSearch = 
      order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateOrderStats = () => {
    return {
      total: orders.length,
      pending: orders.filter(o => o.status === 'pending').length,
      processing: orders.filter(o => o.status === 'processing').length,
      shipped: orders.filter(o => o.status === 'shipped').length,
      delivered: orders.filter(o => o.status === 'delivered').length,
      cancelled: orders.filter(o => o.status === 'cancelled').length,
      revenue: orders
        .filter(o => o.status !== 'cancelled')
        .reduce((sum, o) => sum + (o.totalAmount || 0), 0)
    };
  };

  const stats = calculateOrderStats();

  return (
    <>
      <AdminNavigation />
      <div className="order-management-container">
        <div className="order-header">
          <h1>📦 Order Management</h1>
          <p>Manage and track all customer orders</p>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <h3>{stats.total}</h3>
              <p>Total Orders</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⏳</div>
            <div className="stat-info">
              <h3>{stats.pending}</h3>
              <p>Pending</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚙️</div>
            <div className="stat-info">
              <h3>{stats.processing}</h3>
              <p>Processing</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🚚</div>
            <div className="stat-info">
              <h3>{stats.shipped}</h3>
              <p>Shipped</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <h3>{stats.delivered}</h3>
              <p>Delivered</p>
            </div>
          </div>
          <div className="stat-card highlight">
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <h3>{stats.revenue.toLocaleString()} ৳</h3>
              <p>Total Revenue</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by order number, customer name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}
            >
              All Orders
            </button>
            <button 
              className={`filter-btn ${filterStatus === 'pending' ? 'active' : ''}`}
              onClick={() => setFilterStatus('pending')}
            >
              ⏳ Pending
            </button>
            <button 
              className={`filter-btn ${filterStatus === 'processing' ? 'active' : ''}`}
              onClick={() => setFilterStatus('processing')}
            >
              ⚙️ Processing
            </button>
            <button 
              className={`filter-btn ${filterStatus === 'shipped' ? 'active' : ''}`}
              onClick={() => setFilterStatus('shipped')}
            >
              🚚 Shipped
            </button>
            <button 
              className={`filter-btn ${filterStatus === 'delivered' ? 'active' : ''}`}
              onClick={() => setFilterStatus('delivered')}
            >
              ✅ Delivered
            </button>
          </div>
        </div>

        {/* Orders List */}
        <div className="orders-section">
          <h2>All Orders ({filteredOrders.length})</h2>
          
          {loading ? (
            <div className="loading">Loading orders...</div>
          ) : filteredOrders.length === 0 ? (
            <div className="no-orders">
              <p>No orders found</p>
            </div>
          ) : (
            <>
              <div className="orders-list">
                {currentOrders.map(order => (
                  <div key={order._id} className="order-card">
                    <div className="order-header-info">
                      <div className="order-number">
                        <strong>Order #{order.orderNumber || order._id.slice(-8)}</strong>
                        <span className="order-date">{formatDate(order.createdAt)}</span>
                      </div>
                      <div 
                        className="order-status-badge"
                        style={{ backgroundColor: getStatusColor(order.status) }}
                      >
                        {getStatusIcon(order.status)} {order.status?.toUpperCase()}
                      </div>
                    </div>

                    <div className="order-content">
                      <div className="customer-info">
                        <h4>👤 Customer Details</h4>
                        <p><strong>Name:</strong> {order.customer?.name || 'N/A'}</p>
                        <p><strong>Email:</strong> {order.customer?.email || 'N/A'}</p>
                        <p><strong>Phone:</strong> {order.customer?.phone || 'N/A'}</p>
                        <p><strong>Address:</strong> {order.shippingAddress?.address || 'N/A'}</p>
                      </div>

                      <div className="order-items">
                        <h4>📦 Items ({order.items?.length || 0})</h4>
                        {order.items?.map((item, index) => (
                          <div key={index} className="order-item" style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #eee' }}>
                            <img 
                              src={getImageUrl(item.image)} 
                              alt={item.productName || item.name} 
                              style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }}
                              onError={(e) => e.target.src = '/img/products/f1.jpg'}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: '600' }}>
                                <span>{item.productName || item.name}</span>
                                <span style={{ fontWeight: 'normal', color: '#666' }}>x{item.quantity}</span>
                                <span>{item.price} ৳</span>
                              </div>
                              {(item.color || item.size) && (
                                <div style={{ display: 'flex', gap: '10px', fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                  {item.color && <span>🎨 Color: <strong>{item.color}</strong></span>}
                                  {item.size && <span>📏 Size: <strong>{item.size}</strong></span>}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="order-summary">
                        <h4>💰 Order Summary</h4>
                        {(() => {
                          const shipping = order.shippingAddress?.city?.toLowerCase() === 'khulna' ? 80 : (order.shippingFee || 130);
                          const subtotal = order.totalAmount - shipping;
                          return (
                            <>
                              <div className="summary-row">
                                <span>Subtotal:</span>
                                <span>{subtotal || 0} ৳</span>
                              </div>
                              <div className="summary-row">
                                <span>Shipping ({order.shippingAddress?.city || 'Unknown'}):</span>
                                <span>{shipping} ৳</span>
                              </div>
                            </>
                          );
                        })()}
                        <div className="summary-row total">
                          <span>Total:</span>
                          <span>{order.totalAmount || 0} ৳</span>
                        </div>
                        <div className="payment-method">
                          <span>💳 Payment: {order.paymentMethod || 'Cash on Delivery'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="order-actions">
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(order._id, e.target.value)}
                        className="status-select"
                      >
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      
                      <button 
                        className="btn-delete-order"
                        onClick={() => handleDeleteOrder(order._id)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </button>

                  <div className="pagination-numbers">
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        className={`pagination-number ${currentPage === index + 1 ? 'active' : ''}`}
                        onClick={() => paginate(index + 1)}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>

                  <button
                    className="pagination-btn"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderManagement;